import React, { useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { LocationProvider } from './context/LocationContext';
import { BrandProvider, useBrand, BRAND_META } from './context/BrandContext';
import { useAuth } from './context/AuthContext';
import { useLocation as useLocationCtx } from './context/LocationContext';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import KitchenDetailPage from './pages/KitchenDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ProfilePage from './pages/ProfilePage';
import AddressesPage from './pages/AddressesPage';
import FavoritesPage from './pages/FavoritesPage';
import SupportPage from './pages/SupportPage';
import SupportTicketDetailPage from './pages/SupportTicketDetailPage';
import SecurityPage from './pages/SecurityPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import './customer.css';

// Session keys written by BasicAuth.js
const SESSION_KEY_USER      = 'basicAuthUser';
const SESSION_KEY_OPS_TOKEN = 'opsAccessToken';
const SESSION_KEY_OPS_DATA  = 'opsCustomerData';

// All valid brand-ops user keys (kept in sync with BasicAuth.js)
const BRAND_OPS_USERS = ['biryani_operations', 'biryani_bawarchi', 'biryani_paradise'];


const CustomerLayout = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading } = useAuth();
  const hideNavbar = ['/customer/login', '/customer/register'].includes(location.pathname);
  const autoLoginAttempted = useRef(false);

  // ── Auto-login for Brand Ops accounts (Nawabi / Bawarchi / Paradise) ────────
  // When BasicAuth grants access with account_type='operations', it stores
  // the customer access_token in sessionStorage. We read it here and log in
  // automatically — the ops user never sees the OTP/phone login screen.
  useEffect(() => {
    if (isLoading || isAuthenticated || autoLoginAttempted.current) return;

    const gatewayUser = sessionStorage.getItem(SESSION_KEY_USER);
    if (BRAND_OPS_USERS.includes(gatewayUser)) {
      const opsToken    = sessionStorage.getItem(SESSION_KEY_OPS_TOKEN);
      const opsDataRaw  = sessionStorage.getItem(SESSION_KEY_OPS_DATA);

      if (opsToken) {
        autoLoginAttempted.current = true;
        try {
          const opsCustomer = opsDataRaw ? JSON.parse(opsDataRaw) : {};
          login(opsToken, opsCustomer);
          // If the user landed on the login page, redirect to home
          if (location.pathname.includes('/login') || location.pathname.includes('/register')) {
            navigate('/customer', { replace: true });
          }
        } catch (e) {
          console.error('[CustomerApp] Brand-ops auto-login failed:', e);
        }
        return;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  return (
    <div className="customer-app">
      {!hideNavbar && <Navbar />}
      <main className={`customer-main ${hideNavbar ? 'no-navbar' : ''}`}>
        <Routes>
          <Route path="/" element={<AboutBiryaniPage />} />
          <Route path="/kitchens" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<LoginPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/kitchen/:kitchenId" element={<KitchenDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:orderId" element={<OrderDetailPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/addresses" element={<AddressesPage />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/support/tickets/:ticketId" element={<SupportTicketDetailPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/notification-settings" element={<NotificationSettingsPage />} />
          <Route path="/about" element={<AboutBiryaniPage />} />
          <Route path="/:locationSlug/search" element={<SearchPage />} />
          <Route path="/:locationSlug/cart" element={<CartPage />} />
          <Route path="/:locationSlug/checkout" element={<CheckoutPage />} />
          <Route path="/:locationSlug/order-confirmation" element={<OrderConfirmationPage />} />
          <Route path="/:locationSlug/:restaurantSlug" element={<KitchenDetailPage />} />
          <Route path="/:locationSlug" element={<HomePage />} />
        </Routes>
      </main>
    </div>
  );
};

const AboutBiryaniPage = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useLocationCtx();
  const { brand, meta } = useBrand();

  // Brand-specific content
  const brandName     = meta?.name     || 'Nawabi';
  const brandFullName = meta?.fullName || 'Nawabi Hyderabad House';
  const brandEmoji    = meta?.emoji    || '🍛';

  const BRAND_STORIES = {
    NAWABI: {
      tag:      '🍛 Nawabi Hyderabad House',
      heading:  'About Nawabi Hyderabad House',
      subtitle: 'From the City of Nawabs — authentic Hyderabadi cuisine crafted with tradition and served with love.',
      storyHeading: 'The Story of Nawabi Hyderabad House',
      story: [
        'Nawabi Hyderabad House was founded in 2013 in Houston, Texas — born out of a deep love for authentic Hyderabadi cuisine and a belief that America deserved real Biryani.',
        'Our very first restaurant became an instant success. Customers recognised the difference immediately: slow-cooked Dum Biryani, fresh masalas, and the unmistakable aroma of saffron and whole spices that define true Hyderabadi cooking.',
        'Inspired by our customers\' passion — many of whom became our first franchisees at our Woodlands, TX location — we began expanding. Today, Nawabi Hyderabad House operates across multiple locations in the USA and Canada, with every kitchen upholding the same unwavering standard of quality and consistency.',
        'We Proudly Claim that Nawabi Hyderabad House is one of the very few Restaurant chains in the entire USA that serves authentic Hyderabadi, Andhra, Chennai, Mughlai, and North Indian delicacies under one roof.',
        'Quality is our tradition. Every dish is prepared fresh, every spice is measured with care, and every customer is treated like a guest in our home.',
        'Onsite & Offsite Catering Available.',
      ],
      values: [
        { icon: '🏆', title: 'Quality Is Our Tradition', text: 'At Nawabi Hyderabad House, quality is not a promise — it is a tradition. From hand-ground masalas to slow-cooked Dum Biryani, every dish reflects our commitment to serving only the best.' },
        { icon: '🎉', title: 'We Cater the Happiness', text: 'Nawabi Hyderabad House offers full-service catering for Weddings, Birthday Parties, Corporate Events, and all special occasions — serving up to 5,000 guests with fresh, piping-hot dishes.' },
        { icon: '�', title: 'Authentic Across Every Location', text: 'Whether you visit our Houston flagship or any other location in the USA and Canada, the Biryani tastes exactly the same — because we never cut corners on ingredients, recipes, or preparation.' },
        { icon: '❤️', title: 'A Home Away From Home', text: 'For the South Asian community in North America, Nawabi Hyderabad House is more than a restaurant — it\'s a taste of home. We bring the flavors of Hyderabad, Andhra, and beyond to your city.' },
      ],
      exploreHeading: 'Ready to Order?',
      exploreSub:     'Find a Nawabi Hyderabad House kitchen near you and experience authentic flavors from the City of Nawabs.',
      ctaLabel:       'Explore Nawabi Kitchens →',
    },
    BAWARCHI: {
      tag:      '🫕 Bawarchi Restaurant',
      heading:  'About Bawarchi',
      subtitle: 'Hyderabad\'s most beloved Biryani — crafted with soul, served with pride.',
      storyHeading: 'The Story of Bawarchi',
      story: [
        'Bawarchi Restaurant was established in 1977 in RTC X Roads, Hyderabad — a tiny eatery with a big dream and an even bigger passion for authentic Hyderabadi Biryani.',
        'Within years, word spread across the city about the unique dum-cooked Biryani that set Bawarchi apart. The slow-cooked rice, perfectly marinated mutton, and the signature saffron aroma became irresistible to food lovers from every corner of Hyderabad.',
        'Decades later, Bawarchi remains a household name — not just in Hyderabad, but among the Biryani-loving diaspora worldwide. Every restaurant location carries forward the same original recipe, the same spice balance, and the same warmth that made the first outlet legendary.',
        'Bawarchi\'s Biryani is more than a dish — it is a memory. A first taste. A Sunday ritual. A celebration. From Hyderabad weddings to family lunches, Bawarchi has been a part of every milestone.',
        'We believe great Biryani needs no shortcuts. Every pot is sealed with dough, every grain is cooked to perfection, and every plate is a tribute to Hyderabad\'s grand culinary heritage.',
        'Onsite & Offsite Catering Available.',
      ],
      values: [
        { icon: '🏆', title: 'Original Since 1977', text: 'Bawarchi\'s recipe has remained unchanged since 1977 — the same secret spice blend, the same dum cooking method, the same unforgettable taste that made it Hyderabad\'s most iconic Biryani destination.' },
        { icon: '🎉', title: 'Catering for Every Celebration', text: 'From intimate family dinners to grand weddings, Bawarchi caters events of all sizes. Authentic Biryani and a full spread of Hyderabadi specialties delivered fresh and hot to your venue.' },
        { icon: '🌿', title: 'Quality Without Compromise', text: 'Fresh ingredients, hand-ground masalas, and traditional Dum Pukht technique — every Bawarchi kitchen follows the same unwavering standards that have earned trust for over four decades.' },
        { icon: '❤️', title: 'A Community Institution', text: 'Bawarchi is not just a restaurant — it is a Hyderabadi institution. Generations of families have grown up eating here, making it not just food but a cherished shared memory.' },
      ],
      exploreHeading: 'Taste the Original Bawarchi',
      exploreSub:     'Find a Bawarchi kitchen near you and experience Hyderabad\'s most iconic Biryani.',
      ctaLabel:       'Explore Bawarchi Kitchens →',
    },
    PARADISE: {
      tag:      '🍲 Paradise Biryani',
      heading:  'About Paradise',
      subtitle: 'The restaurant that put Hyderabadi Biryani on the world map.',
      storyHeading: 'The Legend of Paradise',
      story: [
        'Paradise Restaurant opened its doors in 1953 on Secundrabad\'s MG Road — and changed the way the world thinks about Biryani. What began as a modest eatery quickly became a landmark, drawing locals, dignitaries, and travelers with one simple promise: the finest Hyderabadi Biryani ever made.',
        'The founders developed a proprietary Dum cooking technique — layering marinated meat, fragrant basmati rice, caramelised onions, and whole spices, then sealing the handi with dough and slow-cooking it over a low flame until every grain absorbs the rich aromas within.',
        'Over seventy years, Paradise has served Presidents, Prime Ministers, Bollywood stars, and millions of ordinary families who deserve something extraordinary. The restaurant has expanded to dozens of outlets across India and internationally, yet every plate still tastes like it was made in that original Secundrabad kitchen.',
        'Paradise is the answer when someone asks, "Where should I eat Biryani?" — in Hyderabad, in India, and increasingly, across the globe.',
        'We are not just a restaurant. We are a legacy. We are the benchmark. We are Paradise.',
        'Onsite & Offsite Catering Available.',
      ],
      values: [
        { icon: '🏆', title: 'A Legacy Since 1953', text: 'For over 70 years, Paradise has set the gold standard for Hyderabadi Biryani. Our original recipe, unchanged since 1953, has won the loyalty of millions across the world.' },
        { icon: '🎉', title: 'Grand Event Catering', text: 'Paradise brings its legendary Biryani to your celebration. Whether it\'s a royal wedding, a corporate gala, or an intimate gathering, we cater with the same quality and care that defines Paradise.' },
        { icon: '🌿', title: 'Premium Ingredients Only', text: 'Long-grain basmati rice, fresh-cut meat, hand-picked spices sourced from trusted farms — every Paradise Biryani starts with the best ingredients, handled by chefs trained in the Dum Pukht tradition.' },
        { icon: '🌍', title: 'Hyderabad\'s Ambassador to the World', text: 'Paradise has carried Hyderabadi cuisine to every corner of the globe. Every outlet, whether in India or abroad, is a piece of Hyderabad — a warm, aromatic, and unforgettable slice of home.' },
      ],
      exploreHeading: 'Experience the Legend',
      exploreSub:     'Find a Paradise kitchen near you and taste the Biryani that started it all.',
      ctaLabel:       'Explore Paradise Kitchens →',
    },
  };

  const content = BRAND_STORIES[brand] || BRAND_STORIES.NAWABI;

  const handleExplore = () => {
    if (selectedLocation?.slug) {
      navigate(`/customer/${selectedLocation.slug}`);
    } else {
      navigate('/customer/kitchens');
    }
  };

  return (
    <div className="about-biryani-page">
      {/* Hero */}
      <section className="about-hero-section">
        <div className="about-hero-tag">{content.tag}</div>
        <h1 className="about-hero-title">{content.heading}</h1>
        <p className="about-hero-subtitle">{content.subtitle}</p>
      </section>

      <div className="about-content-wrap">
        {/* Story Section */}
        <section className="about-story-section">
          <div className="about-section-label">Our Partner Kitchen</div>
          <h2 className="about-section-heading">{content.storyHeading}</h2>
          {content.story.map((para, i) => (
            <p key={i} className="about-story-text">
              {i === content.story.length - 1 ? <strong>{para}</strong> : para}
            </p>
          ))}

          {/* About images */}
          <div className="about-images-grid">
            <div className="about-img-frame">
              <img
                src="/about-biryani-1.jpg"
                alt={`${brandName} – Authentic Biryani`}
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=800&h=600&fit=crop';
                }}
              />
            </div>
            <div className="about-img-frame">
              <img
                src="/about-biryani-2.jpg"
                alt={`${brandName} – Restaurant Experience`}
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=800&h=600&fit=crop';
                }}
              />
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="about-values-section">
          <div className="about-section-label">What We Stand For</div>
          <div className="about-values-grid">
            {content.values.map((v, i) => (
              <div key={i} className="about-value-card">
                <div className="about-value-icon">{v.icon}</div>
                <div className="about-value-title">{v.title}</div>
                <div className="about-value-text">{v.text}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Explore CTA */}
        <section className="about-explore-section">
          <h2 className="about-explore-heading">{content.exploreHeading}</h2>
          <p className="about-explore-sub">{content.exploreSub}</p>
          <button className="about-explore-btn" onClick={handleExplore}>
            {content.ctaLabel}
          </button>
        </section>
      </div>
    </div>
  );
};


const CustomerApp = () => {
  return (
    <BrandProvider>
      <AuthProvider>
        <CartProvider>
          <LocationProvider>
            <CustomerLayout />
          </LocationProvider>
        </CartProvider>
      </AuthProvider>
    </BrandProvider>
  );
};

export default CustomerApp;
